import React, { useEffect, useContext, useState, useRef } from 'react';
import { useActor } from '@xstate/react';
import { GlobalStateContext } from '../state/app/index';
import * as FlexLayout from 'flexlayout-react';

import { isMobile } from '../utils/helpers.ts';
import logger from '../logger';
import { getFactory } from '../utils/panelHelper';
import interactionLedger from '../utils/interactionLedger';
import {
  getActivePanelFromFlexLayout,
  getPanelLayoutDebugInfo,
} from '../utils/getActivePanel';
import useTabPrefetching from '../hooks/useTabPrefetching';

const DryChrome = (props) => {
  /*
   * Pull in the entire appState Machine and child machines
   */
  const globalServices = useContext(GlobalStateContext);
  const [appState, sendAppState] = useActor(globalServices.appState);
  const { breakpoint, activeFlexLayoutModel } = appState.context;

  // Track last set of active panels for ledger logging
  const lastActivePanelsRef = useRef([]);

  // const [delayCompleted, setDelayCompleted] = useState(false);

  // useEffect(() => {
  //   const timeoutId = setTimeout(() => {
  //     setDelayCompleted(true);
  //   }, 3000); // 5 seconds delay

  //   return () => clearTimeout(timeoutId); // Cleanup function to avoid memory leaks
  // }, []);

  // useEffect(() => {
  //   if (delayCompleted) {
  //     sendAppState({ type: 'GetChromeModelRunning' });
  //   }
  // }, [delayCompleted]);

  // useEffect(() => {
  //   logger.info('DryChrome - DryChromeClass useEffect', activeChromeModel);
  // }, [activeChromeModel]);

  // useEffect(() => {
  //   logger.info(appState.context, 'DryChromeClass Context');
  // }, [appState.context]);

  // @todo - Move to a dedicated xState machine and/or event
  const fontObject = React.useMemo(() => 
    isMobile(breakpoint) ? { size: '1.5em' } : { size: '1em' }, 
  [breakpoint]);

  const handleModelChange = React.useCallback((newModel) => {
    logger.info(
      {
        newModel,
      },
      'onModelChange'
    );

    // Detect panel selection changes and log them to the interaction ledger
    try {
      const activePanels = getActivePanelFromFlexLayout(newModel);
      const previousPanels = Array.isArray(lastActivePanelsRef.current)
        ? lastActivePanelsRef.current
        : [];

      const panelsChanged =
        JSON.stringify(previousPanels) !== JSON.stringify(activePanels);

      if (panelsChanged) {
        const panelDisplayMap = {
          info: 'Info',
          notes: 'Notes',
          editor: 'Editor',
          notebooks: 'Notebooks',
          aof: 'Areas of Focus',
          'open-notes': 'Open Notes',
        };

        const previousLabels =
          previousPanels.length > 0
            ? previousPanels
                .map((panelId) => panelDisplayMap[panelId] || panelId)
                .join(', ')
            : 'none';

        const currentLabels =
          activePanels.length > 0
            ? activePanels
                .map((panelId) => panelDisplayMap[panelId] || panelId)
                .join(', ')
            : 'none';

        interactionLedger.log(
          'panelSelection',
          'ui',
          {
            previousPanels,
            currentPanels: activePanels,
          },
          `Panels changed: ${previousLabels} → ${currentLabels}`
        );

        lastActivePanelsRef.current = activePanels;
      }

      // Log detailed layout/column information for debugging
      const layoutInfo = getPanelLayoutDebugInfo(newModel);
      const columnSummaries = layoutInfo.columns.map((column) => {
        const selected =
          column.selectedTabName || column.selectedComponent || 'none';
        return selected;
      });

      const layoutDescription =
        columnSummaries.length > 0 ? columnSummaries.join(' | ') : 'none';

      interactionLedger.log(
        'panelLayout',
        'ui',
        layoutInfo,
        `Panel layout updated (columns left→right): ${layoutDescription}`
      );
    } catch (error) {
      logger.error(
        { error },
        'Failed to log panel selection change to interaction ledger from DryChrome'
      );
    }

    sendAppState({
      type: 'SetModel',
      data: {
        newModel,
        breakpoint,
      },
    });

    return newModel;
  }, [breakpoint, sendAppState]);

  const handleAction = React.useCallback((action) => {
    logger.info({ action }, 'FlexLayout onAction');

    // Temporarily disabled: Action-based model syncing
    // This was causing issues. We'll rely on onModelChange for now.
    // TODO: Find a better way to detect tab selections that doesn't break the app

    sendAppState({
      type: 'ActionReceiver',
      action,
    });

    return action;
  }, [sendAppState]);

  // Enable tab hover prefetching for instant panel loading
  useTabPrefetching();

  if (!activeFlexLayoutModel) {
    return null;
  }

  return (
    <main className="chrome-layout">
      <div className="chrome-layout__content">
        <FlexLayout.Layout
          model={activeFlexLayoutModel}
          factory={getFactory}
          font={fontObject}
          onModelChange={handleModelChange}
          onAction={handleAction}
        />
      </div>

    </main>
  );
};

export default DryChrome;
